import { Stack } from '@mui/material';
import { type ComponentType } from 'react';

import { api, isDefined } from 'common';

import { AgentConversationFormFields } from './FormFields/agentConversationFormFields';
import { AssistantFormFields } from './FormFields/assistantFormFields';
import { EncounterContactsFormFields } from './FormFields/encounterContactsFormFields';
import { EncounterFormFields } from './FormFields/encountersFormFields';
import { ExternalDocumentPacketsFormFields } from './FormFields/externalDocumentPackets';
import { FeedMessagesFormFields } from './FormFields/feedMessagesFormFields';
import { GuarantorFormFields } from './FormFields/guarantorsFormFields';
import { Hl7MessageEventFormFields } from './FormFields/hl7MessageFormFields';
import { InsuranceOcrResultsFormFields } from './FormFields/insuranceOcrResultsFormFields';
import { JobContextFormFields } from './FormFields/jobContextFormFields';
import { PatientFormFields } from './FormFields/patientFormFields';
import { PatientInsuranceInformationFormFields } from './FormFields/patientInsuranceInformationFormFields';
import { PatientOrderFormFields } from './FormFields/patientOrderFormFields';
import { PatientUploadFormFields } from './FormFields/patientUploadFormFields';
import { PaymentFormFields } from './FormFields/paymentFormFields';
import { PracticeFormFields } from './FormFields/practiceFormFields';
import { PrimaryCareProviderFormFields } from './FormFields/primaryCareProviderFormFields';
import { ProviderFormFields } from './FormFields/providerFormFields';
import { ReferenceTableFormFields } from './FormFields/referenceTableFormFields';
import { SftpMessageEventFormFields } from './FormFields/sftpMessageFormFields';
import { TemplateFormFields } from './FormFields/templateFormFields';
import { X12MessageEventFormFields } from './FormFields/x12MessageFormFields';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const NotableObject = api.FlowDefinition.NotableObject;
type NotableObject = api.FlowDefinition.NotableObject;

const GET_NOTABLE_OBJECT_FORM_FIELDS: Partial<Record<NotableObject, ComponentType>> = {
  [NotableObject.AgentConversation]: AgentConversationFormFields,
  [NotableObject.Guarantor]: GuarantorFormFields,
  [NotableObject.Payment]: PaymentFormFields,
  [NotableObject.Template]: TemplateFormFields,
  [NotableObject.Hl7MessageEvent]: Hl7MessageEventFormFields,
  [NotableObject.ReferenceTable]: ReferenceTableFormFields,
  [NotableObject.PatientOrder]: PatientOrderFormFields,
  [NotableObject.Provider]: ProviderFormFields,
  [NotableObject.ExternalDocumentPacket]: ExternalDocumentPacketsFormFields,
  [NotableObject.PatientInsuranceInformation]: PatientInsuranceInformationFormFields,
  [NotableObject.Patient]: PatientFormFields,
  [NotableObject.Practice]: PracticeFormFields,
  [NotableObject.SftpMessageEvent]: SftpMessageEventFormFields,
  [NotableObject.X12MessageEvent]: X12MessageEventFormFields,
  [NotableObject.PatientUpload]: PatientUploadFormFields,
  [NotableObject.Encounter]: EncounterFormFields,
  [NotableObject.PrimaryCareProvider]: PrimaryCareProviderFormFields,
  [NotableObject.FeedMessages]: FeedMessagesFormFields,
  [NotableObject.EncounterContacts]: EncounterContactsFormFields,
  [NotableObject.InsuranceOcrResults]: InsuranceOcrResultsFormFields,
  [NotableObject.JobContext]: JobContextFormFields,
  [NotableObject.Assistant]: AssistantFormFields,
};

export function GetNotableObjectFormFields() {
  const { value: notableObject } =
    useSelectedNodeField<api.FlowDefinition.GetNotableObjectConfig['notableObject']>(
      'config.notableObject'
    );
  const excludedObjects = [
    NotableObject.ServiceReviewSubmissions,
    NotableObject.AuthorizationSubmissions,
  ];
  const options = Object.values(NotableObject)
    .filter((notableObject) => !excludedObjects.includes(notableObject))
    .map((object) => ({
      label: object,
      value: object,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const FormFields = isDefined(notableObject)
    ? GET_NOTABLE_OBJECT_FORM_FIELDS[notableObject]
    : undefined;
  return (
    <>
      <BlockSelectField
        label="Notable Object"
        fieldName="config.notableObject"
        required
        options={options}
      />
      {isDefined(FormFields) && (
        <Stack gap={3}>
          {FormFields && (
            <>
              <FormFields />
            </>
          )}
        </Stack>
      )}
    </>
  );
}
